/**
 * Deal Flow Redux Actions
 *
 * Actions for admin/evaluator rating system and interest stats viewing.
 * Follows existing admin action patterns with thunk middleware.
 */

import axios from "axios";
import Swal from "sweetalert2";
import {
  ADMIN_SUBMIT_RATING_REQUEST,
  ADMIN_SUBMIT_RATING_SUCCESS,
  ADMIN_SUBMIT_RATING_FAIL,
  ADMIN_GET_RATINGS_REQUEST,
  ADMIN_GET_RATINGS_SUCCESS,
  ADMIN_GET_RATINGS_FAIL,
  ADMIN_GET_INTEREST_STATS_REQUEST,
  ADMIN_GET_INTEREST_STATS_SUCCESS,
  ADMIN_GET_INTEREST_STATS_FAIL,
  ADMIN_RECALCULATE_STATS_REQUEST,
  ADMIN_RECALCULATE_STATS_SUCCESS,
  ADMIN_RECALCULATE_STATS_FAIL,
  ADMIN_DEAL_FLOW_CLEAR_ERROR,
} from "../constants/dealFlowConstants";

/**
 * Submit admin or evaluator rating for a campaign
 * Rating type (ADMIN/EVALUATOR) is derived from user role on backend
 *
 * @param {number} campaignId - Campaign to rate
 * @param {number} rating - 1-5 star rating (5 = low risk, 1 = high risk)
 * @param {string} notes - Optional rating justification
 */
export const submitAdminRating =
  (campaignId, rating, notes = "") =>
  async (dispatch) => {
    try {
      dispatch({ type: ADMIN_SUBMIT_RATING_REQUEST });

      const token = localStorage.getItem("accessToken");
      const config = {
        headers: {
          "Content-type": "application/json",
          "x-access-token": token,
        },
      };

      const { data } = await axios.post(
        process.env.REACT_APP_API + "admin/deal-flow/ratings",
        { campaign_id: campaignId, rating, notes },
        config
      );

      const submittedRating = data?.data?.rating ?? rating;
      const avgFromResponse = data?.data?.campaign_average;

      dispatch({
        type: ADMIN_SUBMIT_RATING_SUCCESS,
        payload: {
          campaignId,
          rating: submittedRating,
          ...(typeof avgFromResponse === "number" && { campaignAverage: avgFromResponse }),
        },
      });

      Swal.fire({
        title: "Rating Submitted",
        text: `Your ${rating}-star rating has been saved.`,
        timer: 2500,
        timerProgressBar: true,
        icon: "success",
        confirmButtonText: "OK",
        showCloseButton: true,
      });

      return data;
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || error.message || "Failed to submit rating";

      Swal.fire({
        title: "Rating Error",
        text: errorMessage,
        timer: 3000,
        timerProgressBar: true,
        icon: "error",
        confirmButtonText: "OK",
        showCloseButton: true,
      });

      dispatch({
        type: ADMIN_SUBMIT_RATING_FAIL,
        payload: errorMessage,
      });

      throw error;
    }
  };

/**
 * Get full ratings breakdown for a campaign
 * Returns admin, evaluator, and investor averages plus weighted average
 *
 * @param {number} campaignId - Campaign ID
 */
export const getCampaignRatingsAdmin = (campaignId) => async (dispatch) => {
  try {
    dispatch({ type: ADMIN_GET_RATINGS_REQUEST, payload: { campaignId } });

    const token = localStorage.getItem("accessToken");
    const config = {
      headers: {
        "x-access-token": token,
      },
    };

    const { data } = await axios.get(
      process.env.REACT_APP_API + `admin/deal-flow/ratings/${campaignId}`,
      config
    );

    dispatch({
      type: ADMIN_GET_RATINGS_SUCCESS,
      payload: { campaignId, ratings: data.data },
    });

    return data;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message || error.message || "Failed to fetch ratings";

    dispatch({
      type: ADMIN_GET_RATINGS_FAIL,
      payload: errorMessage,
    });

    // Don't show Swal for fetch errors - just log
    console.error("Failed to fetch campaign ratings:", errorMessage);
  }
};

/**
 * Get interest stats for a campaign (favorites, soft commitments)
 * Admin can view stats for any campaign (unlike Portal which restricts to owner)
 *
 * @param {number} campaignId - Campaign ID
 */
export const getInterestStats = (campaignId) => async (dispatch) => {
  try {
    dispatch({ type: ADMIN_GET_INTEREST_STATS_REQUEST, payload: { campaignId } });

    const token = localStorage.getItem("accessToken");
    const config = {
      headers: {
        "x-access-token": token,
      },
    };

    const { data } = await axios.get(
      process.env.REACT_APP_API + `admin/deal-flow/campaign/${campaignId}/interest-stats`,
      config
    );

    dispatch({
      type: ADMIN_GET_INTEREST_STATS_SUCCESS,
      payload: { campaignId, stats: data.data },
    });

    return data;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message || error.message || "Failed to fetch interest stats";

    dispatch({
      type: ADMIN_GET_INTEREST_STATS_FAIL,
      payload: errorMessage,
    });

    console.error("Failed to fetch interest stats:", errorMessage);
  }
};

/**
 * Recalculate campaign statistics (ratings, favorites, commitments)
 * Useful if data seems out of sync
 *
 * @param {number} campaignId - Campaign ID
 */
export const recalculateStats = (campaignId) => async (dispatch) => {
  try {
    dispatch({ type: ADMIN_RECALCULATE_STATS_REQUEST, payload: { campaignId } });

    const token = localStorage.getItem("accessToken");
    const config = {
      headers: {
        "Content-type": "application/json",
        "x-access-token": token,
      },
    };

    const { data } = await axios.post(
      process.env.REACT_APP_API + `admin/deal-flow/campaign/${campaignId}/recalculate-stats`,
      {},
      config
    );

    dispatch({
      type: ADMIN_RECALCULATE_STATS_SUCCESS,
      payload: { campaignId, stats: data },
    });

    Swal.fire({
      title: "Stats Recalculated",
      text: "Campaign statistics have been refreshed.",
      timer: 2000,
      timerProgressBar: true,
      icon: "success",
      confirmButtonText: "OK",
      showCloseButton: true,
    });

    // Refresh both ratings and interest stats after recalculation
    dispatch(getCampaignRatingsAdmin(campaignId));
    dispatch(getInterestStats(campaignId));

    return data;
  } catch (error) {
    const errorMessage =
      error.response?.data?.message || error.message || "Failed to recalculate stats";

    Swal.fire({
      title: "Recalculation Error",
      text: errorMessage,
      timer: 3000,
      timerProgressBar: true,
      icon: "error",
      confirmButtonText: "OK",
      showCloseButton: true,
    });

    dispatch({
      type: ADMIN_RECALCULATE_STATS_FAIL,
      payload: errorMessage,
    });
  }
};

/**
 * Clear any deal flow errors
 */
export const clearDealFlowError = () => (dispatch) => {
  dispatch({ type: ADMIN_DEAL_FLOW_CLEAR_ERROR });
};
